import { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/layout/DashboardLayout';
import { Button, ConfirmDialog, Dialog, Input } from '@/design-system';
import { useAuth } from '@/core/auth';
import { useErrorToast } from '@/core/errors/useErrorToast';
import type { StaffUser } from './api/users.service';
import { useUsersListQuery } from './hooks/useUsersListQuery';
import { useUserMutations } from './hooks/useUserMutations';
import { UserCreateDialog } from './components/UserCreateDialog';
import { UserEditDialog } from './components/UserEditDialog';
import { UserFilters } from './components/UserFilters';
import { UserTable, type UserTableHandlers } from './components/UserTable';

export function UsersPage() {
  const onErrorToast = useErrorToast();
  const { user: currentUser } = useAuth();
  const { createUser, updateUser, deleteUser, toggleUserActive, anonymizeUser } = useUserMutations();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [isActiveFilter, setIsActiveFilter] = useState<boolean | undefined>(undefined);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAnonymizeDialog, setShowAnonymizeDialog] = useState(false);
  const [anonymizeConfirmText, setAnonymizeConfirmText] = useState('');
  const [selectedUser, setSelectedUser] = useState<StaffUser | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const listFilters = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      role: roleFilter || undefined,
      isActive: isActiveFilter,
    }),
    [page, limit, debouncedSearch, roleFilter, isActiveFilter]
  );

  const { data: usersData, isLoading, isError, error } = useUsersListQuery(listFilters);
  const users = usersData?.data ?? [];
  const totalPages = usersData?.pagination.totalPages ?? 1;
  const total = usersData?.pagination.total ?? 0;

  useEffect(() => {
    if (isError && error) onErrorToast(error, 'Erro ao carregar usuários');
  }, [isError, error, onErrorToast]);

  const canCreate = currentUser?.role === 'SUPERADMIN';
  const canDelete = currentUser?.role === 'SUPERADMIN';

  const handleToggleActive = useCallback(
    (u: StaffUser) => {
      void toggleUserActive.mutateAsync({ id: u.id, isActive: !u.isActive });
    },
    [toggleUserActive]
  );

  const tableHandlers = useMemo<UserTableHandlers>(
    () => ({
      onCreate: () => setShowCreateDialog(true),
      onEdit: (u) => {
        setSelectedUser(u);
        setShowEditDialog(true);
      },
      onDelete: (u) => {
        setSelectedUser(u);
        setShowDeleteDialog(true);
      },
      onAnonymize: (u) => {
        setSelectedUser(u);
        setAnonymizeConfirmText('');
        setShowAnonymizeDialog(true);
      },
      onToggleActive: handleToggleActive,
    }),
    [handleToggleActive]
  );

  return (
    <DashboardLayout title="Usuários">
      <div className="space-y-4">
        <UserTable
          filters={<UserFilters roleFilter={roleFilter} isActiveFilter={isActiveFilter}
            onRoleChange={(value) => { setRoleFilter(value); setPage(1); }}
            onActiveChange={(value) => { setIsActiveFilter(value); setPage(1); }} />}
          users={users}
          loading={isLoading}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={setPage}
          search={search}
          onSearchChange={setSearch}
          canCreate={canCreate}
          canDelete={canDelete}
          currentUserId={currentUser?.id}
          handlers={tableHandlers}
        />
      </div>

      {canCreate && (
        <UserCreateDialog
          isOpen={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          createUser={createUser}
          currentUserRole={currentUser?.role}
        />
      )}

      <UserEditDialog
        isOpen={showEditDialog}
        user={selectedUser}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedUser(null);
        }}
        updateUser={updateUser}
        currentUserRole={currentUser?.role}
      />

      {canDelete && (
        <ConfirmDialog
          isOpen={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setSelectedUser(null);
          }}
          onConfirm={() => {
            if (!selectedUser) return;
            void deleteUser.mutateAsync(selectedUser.id).then(() => {
              setShowDeleteDialog(false);
              setSelectedUser(null);
            });
          }}
          title="Deletar Usuário"
          message={`Tem certeza que deseja deletar o usuário "${selectedUser?.name}"? Soft delete — o registro continua no banco, apenas isActive=false e deletedAt setado.`}
          confirmText="Deletar"
          cancelText="Cancelar"
          variant="danger"
          isLoading={deleteUser.isPending}
        />
      )}

      {/* Anonimização permanente (LGPD): sobrescreve email/name/senha do
          staff_user. Diferente do delete acima, não pode ser revertido. */}
      {canDelete && selectedUser && (
        <Dialog isOpen={showAnonymizeDialog} onClose={() => { if (anonymizeUser.isPending) return; setShowAnonymizeDialog(false); setSelectedUser(null); setAnonymizeConfirmText(''); }} title="Excluir permanentemente (LGPD)" size="sm" footer={(
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm"
                type="button"
                onClick={() => {
                  setShowAnonymizeDialog(false);
                  setSelectedUser(null);
                  setAnonymizeConfirmText('');
                }}
                disabled={anonymizeUser.isPending}
              >
                Cancelar
              </Button>
              <Button variant="danger" size="sm"
                type="button"
                className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  if (anonymizeConfirmText !== 'EXCLUIR PERMANENTEMENTE') return;
                  void anonymizeUser.mutateAsync(selectedUser.id).then(() => {
                    setShowAnonymizeDialog(false);
                    setSelectedUser(null);
                    setAnonymizeConfirmText('');
                  });
                }}
                disabled={
                  anonymizeUser.isPending || anonymizeConfirmText !== 'EXCLUIR PERMANENTEMENTE'
                }
              >
                {anonymizeUser.isPending ? 'Excluindo...' : 'Confirmar exclusão permanente'}
              </Button>
            </div>
        )}>
            <p className="mt-3 text-sm text-gray-700">
              Esta ação <strong>ANONIMIZA</strong> os dados pessoais (nome, email, senha) do staff user <strong>{selectedUser.name}</strong> no banco. Login fica impossível, mesmo via restore.
            </p>
            <p className="mt-2 text-sm font-medium text-rose-700">Esta ação não pode ser desfeita.</p>
            <label className="mt-4 block text-xs font-medium text-gray-600">
              Para confirmar, digite: <code className="font-mono">EXCLUIR PERMANENTEMENTE</code>
            </label>
            <Input
              type="text"
              autoFocus
              className="mt-1"
              placeholder="EXCLUIR PERMANENTEMENTE"
              value={anonymizeConfirmText}
              onChange={(e) => setAnonymizeConfirmText(e.target.value)}
            />
        </Dialog>
      )}
    </DashboardLayout>
  );
}
